import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Container
} from '@mui/material';
import api from '../services/api';

interface StuckField {
    key: string;
    label?: string;
    type?: string;
    sectionTitle?: string;
}

interface StuckData {
    userId: string;
    userName?: string;
    stuckFields: StuckField[];
    status: string;
}

const StuckFieldsResolver: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<StuckData | null>(null);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await api.get<StuckData>(`/welcome-questionnaire/stuck-fields/${userId}`);
                setData(res.data);
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to load stuck fields.');
            } finally {
                setLoading(false);
            }
        };
        if (userId) fetchData();
    }, [userId]);

    const handleChange = (key: string, value: string) => {
        setAnswers(prev => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const res = await api.post(`/welcome-questionnaire/stuck-fields/${userId}/resolve`, {
                answers
            });
            alert(res.data.message || 'Fields resolved successfully!');
            navigate('/'); // Redirect to dashboard/list
        } catch (err) {
            alert('Failed to submit resolution.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data) return <Alert severity="info">No stuck fields found.</Alert>;

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Paper sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>Box Stuck Fields Resolution</Typography>
                <Typography variant="subtitle1" gutterBottom>User Name: {data.userName || data.userId}</Typography>

                <Box sx={{ mt: 3 }}>
                    {Object.entries(
                        data.stuckFields.reduce((acc, field) => {
                            const section = field.sectionTitle || 'General';
                            if (!acc[section]) acc[section] = [];
                            acc[section].push(field);
                            return acc;
                        }, {} as Record<string, StuckField[]>)
                    ).map(([section, fields]) => (
                        <Box key={section} sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ color: '#222222', mb: 2, borderBottom: '1px solid #eee', pb: 1 }}>
                                {section}
                            </Typography>
                            {fields.map(field => (
                                <Box key={field.key} sx={{ mb: 3, pl: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        {field.label || field.key} ({field.type})
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline={field.type === 'textarea'}
                                        rows={field.type === 'textarea' ? 3 : 1}
                                        value={answers[field.key] || ''}
                                        onChange={(e) => handleChange(field.key, e.target.value)}
                                        placeholder="Enter value"
                                    />
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                        Resolve & Save
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default StuckFieldsResolver;
